import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { SupabaseService } from '../../services/supabase.service';
import { AuthService } from '../../services/auth.service';
import { TranslationService } from '../../services/translation.service';
type Answers = {
  elevation_gain_m: number | null;
  distance_km: number | null;
  difficulty: number | null;
  waymarking: number | null;
};
const empty = (): Answers => ({
  elevation_gain_m: null,
  distance_km: null,
  difficulty: null,
  waymarking: null,
});
@Component({
  selector: 'app-runner-statistics',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './runner-statistics.component.html',
})
export class RunnerStatisticsComponent {
  races = input<{ id: string; name: string; distance: string }[]>([]);
  private db = inject(SupabaseService).client;
  auth = inject(AuthService);
  lang = inject(TranslationService);
  registered = computed(() => !!this.auth.currentUser() && !this.auth.currentUser()?.is_anonymous);
  selected = signal('');
  loading = signal(false);
  saving = signal(false);
  error = signal('');
  message = signal('');
  existing = signal(false);
  averages = signal<Record<string, number | null>>({});
  answers: Answers = empty();
  private version = 0;
  fields: {
    key: keyof Answers;
    fr: string;
    en: string;
    unit: string;
    min: number;
    max: number;
    step: number;
  }[] = [
    {
      key: 'elevation_gain_m',
      fr: 'Dénivelé positif (D+)',
      en: 'Elevation gain (D+)',
      unit: 'm',
      min: 0,
      max: 10000,
      step: 1,
    },
    {
      key: 'distance_km',
      fr: 'Distance mesurée',
      en: 'Measured distance',
      unit: 'km',
      min: 0.01,
      max: 300,
      step: 0.01,
    },
    {
      key: 'difficulty',
      fr: 'Difficulté ressentie',
      en: 'Perceived difficulty',
      unit: '/ 5',
      min: 1,
      max: 5,
      step: 1,
    },
    {
      key: 'waymarking',
      fr: 'Qualité du balisage',
      en: 'Waymarking quality',
      unit: '/ 5',
      min: 1,
      max: 5,
      step: 1,
    },
  ];
  text(fr: string, en: string) {
    return this.lang.currentLang() === 'fr' ? fr : en;
  }
  constructor() {
    effect(() => {
      const races = this.races();
      if (!races.some((r) => r.id === this.selected())) this.selected.set(races[0]?.id || '');
    });
    effect(() => {
      const id = this.selected();
      const user = this.auth.currentUser();
      void this.load(id, user && !user.is_anonymous ? user.id : null);
    });
  }
  async load(id: string, userId: string | null) {
    const version = ++this.version;
    this.answers = empty();
    this.existing.set(false);
    this.averages.set({});
    this.error.set('');
    this.message.set('');
    if (!id) {
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    try {
      const [stats, own] = await Promise.all([
        this.db.rpc('get_race_runner_statistics', { p_race_id: id }),
        userId
          ? this.db
              .from('race_runner_contributions')
              .select('elevation_gain_m,distance_km,difficulty,waymarking')
              .eq('race_id', id)
              .eq('user_id', userId)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);
      if (version !== this.version) return;
      if (stats.error) throw stats.error;
      if (own.error) throw own.error;
      this.averages.set(stats.data || {});
      if (own.data) {
        this.answers = { ...own.data };
        this.existing.set(true);
      }
    } catch {
      if (version === this.version)
        this.error.set(
          this.text(
            'Impossible de charger les contributions. Réessayez.',
            'Unable to load contributions. Please retry.',
          ),
        );
    } finally {
      if (version === this.version) this.loading.set(false);
    }
  }
  retry() {
    const u = this.auth.currentUser();
    void this.load(this.selected(), u && !u.is_anonymous ? u.id : null);
  }
  async save(remove = false) {
    const u = this.auth.currentUser(),
      id = this.selected(),
      version = this.version;
    if (!u || u.is_anonymous || !id || this.saving() || this.loading()) return;
    if (!remove && !Object.values(this.answers).some((v) => v !== null)) {
      this.error.set(
        this.text('Renseignez au moins une statistique.', 'Enter at least one statistic.'),
      );
      return;
    }
    this.saving.set(true);
    this.error.set('');
    this.message.set('');
    try {
      const table = this.db.from('race_runner_contributions');
      const result = remove
        ? await table.delete().eq('race_id', id).eq('user_id', u.id)
        : this.existing()
          ? await table
              .update({ ...this.answers })
              .eq('race_id', id)
              .eq('user_id', u.id)
              .select('race_id')
              .single()
          : await table.insert({ ...this.answers, race_id: id, user_id: u.id });
      if (result.error) throw result.error;
      if (version !== this.version) return;
      await this.load(id, u.id);
      if (this.selected() === id && this.auth.currentUser()?.id === u.id && !this.error())
        this.message.set(
          remove
            ? this.text('Contribution retirée.', 'Contribution removed.')
            : this.text('Contribution enregistrée. Merci !', 'Contribution saved. Thank you!'),
        );
    } catch {
      if (version === this.version)
        this.error.set(
          this.text(
            'Enregistrement impossible. Vérifiez les valeurs ou rechargez les contributions.',
            'Unable to save. Check the values or reload contributions.',
          ),
        );
    } finally {
      this.saving.set(false);
    }
  }
}
